import { useFeatureEnabled } from 'api/appConfig';
import { useIdpGuide, useMethodGuide, useScimMethodGuide } from 'api/guide';
import { useIdPs, useXaaIdPs } from 'api/idps';
import { ROUTES, RouteSegments } from 'constants/routes';
import { GuideType, Method, SCIMMethod } from 'consts';
import { useFeatureFlags } from 'contexts/FeatureFlagsContext';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import { useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { S4Feature } from 'types/api';
import { useNav } from 'utils/navigation';
import { matchAppRouteOutsideRoute } from 'utils/routes';

export default () => {
	const location = useLocation();
	const isJitRoute = Boolean(matchPath(ROUTES.jit, location.pathname));
	const isSelectIdPRoute = Boolean(
		matchPath(ROUTES.selectIdP, location.pathname)
	);
	// this hook is not rendered under a route so useParams doesn't work here.
	const pathnameMatch = matchAppRouteOutsideRoute(location.pathname);
	const {
		[RouteSegments.Guide]: routeGuide,
		[RouteSegments.IdPID]: routeIdpID,
		[RouteSegments.Method]: routeMethod,
		[RouteSegments.StepNum]: routeStepNum
	} = pathnameMatch?.params || {};

	const isXaaRoute = routeGuide === GuideType.XAA;

	const nav = useNav();
	const [done, setDone] = useState(false);
	const { data: idpsData } = useIdPs();
	// The XAA content catalog (same /v1/ssotemplates source, filtered to xaa providers) -
	// used to validate the route idp before it is persisted as providerID.
	const { data: xaaIdps } = useXaaIdPs();
	// XAA routes read the guide off the selected provider's template (guides.xaa),
	// exactly like SSO/SCIM read theirs off the route idp's template.
	const { data: idpGuideData } = useIdpGuide(routeIdpID);
	const { data: methodGuideData } = useMethodGuide(
		routeIdpID,
		routeMethod as Method
	);
	const { data: methodScimGuideData } = useScimMethodGuide(
		routeIdpID,
		routeMethod as SCIMMethod
	);
	const xaaGuideData = idpGuideData?.guides.xaa?.[Method.Xaa] ?? null;
	const { enabled: xaaEnabled } = useFeatureEnabled(S4Feature.Xaa);

	const {
		setGuideType,
		setIdPID,
		setMethod,
		setGuideStepNum,
		setScimIdPID,
		setScimMethod,
		setScimGuideStepNum,
		setXaaIdPID,
		setXaaMethod,
		setXaaGuideStepNum
	} = useIdPConfigContext();

	const { ready: featureFlagsReady } = useFeatureFlags();

	const { enabled: ssoEnabled, configLoaded: appConfigLoaded } =
		useFeatureEnabled(S4Feature.Sso);
	// enabled defaults to false while the app config is still loading, so only
	// treat SSO as disabled once the config actually arrived.
	const ssoDisabled = appConfigLoaded && !ssoEnabled;
	const isSSORoute = isSelectIdPRoute || routeGuide === GuideType.SSO;

	useEffect(() => {
		// An SSO route waits for the app config before deciding anything: the feature-flags request
		// can resolve first, and `done` latches for the app's lifetime - so latching here on the
		// "no route params" branch below (which is where /select-idp lands) would mean the redirect
		// never fires and is never re-evaluated. Only SSO routes wait, so a failed config request
		// cannot hang the SCIM and JIT guides.
		if (done || !featureFlagsReady || (isSSORoute && !appConfigLoaded)) {
			return;
		}

		if (isJitRoute) {
			setDone(true);
			return;
		}

		// SSO routes are unreachable when the SSO feature is disabled, e.g. a
		// stale link into a suite that is now SCIM only.
		if (ssoDisabled && isSSORoute) {
			setDone(true);
			nav(ROUTES.home);
			return;
		}

		if (!routeGuide || !routeIdpID || !routeMethod) {
			setDone(true);
			return;
		}

		// basic type validations
		if (!Object.values(GuideType).includes(routeGuide as GuideType)) {
			setDone(true);
			nav(ROUTES.home);
			return;
		}

		if (
			(routeGuide === GuideType.SCIM &&
				!Object.values(SCIMMethod).includes(routeMethod as SCIMMethod)) ||
			(routeGuide === GuideType.SSO &&
				!Object.values(Method).includes(routeMethod as Method))
		) {
			setDone(true);
			nav(ROUTES.home);
			return;
		}

		// XAA is a standalone, feature-gated config whose guide rides on the selected
		// provider's template - resolve it separately and NEVER touch the SSO/SCIM slots.
		if (isXaaRoute) {
			if (!xaaEnabled || routeMethod !== Method.Xaa) {
				setDone(true);
				nav(ROUTES.home);
				return;
			}
			if (!xaaIdps) {
				// wait for the XAA idp catalog to be populated
				return;
			}
			// The route idp must exist in the XAA content catalog - reject an arbitrary id
			// from the URL rather than persisting it as providerID (mirrors the SSO/SCIM
			// idpsData guard below). Checked BEFORE waiting on the guide so a bogus id
			// (whose template fetch never resolves) redirects instead of hanging.
			if (!xaaIdps.find((idp) => idp.id === routeIdpID)) {
				setDone(true);
				nav(ROUTES.guideNotFound);
				return;
			}
			if (!xaaGuideData) {
				// wait for the selected provider's xaa guide to be populated
				return;
			}
			const stepNum = Number(routeStepNum);
			if (
				Number.isNaN(stepNum) ||
				stepNum < 1 ||
				xaaGuideData.guide.length < stepNum
			) {
				setDone(true);
				nav(ROUTES.guideNotFound);
				return;
			}
			setDone(true);
			setGuideType(GuideType.XAA);
			setXaaIdPID(routeIdpID);
			setXaaMethod(Method.Xaa);
			setXaaGuideStepNum(stepNum);
			return;
		}

		if (routeStepNum && Number.isNaN(Number(routeStepNum))) {
			setDone(true);
			nav(ROUTES.home);
			return;
		}

		// correctness validations
		if (!idpsData) {
			// wait for idpsData to be populated
			return;
		}

		if (!idpsData.data.find((idp) => idp.id === routeIdpID)) {
			setDone(true);
			nav(ROUTES.guideNotFound);
			return;
		}

		/* istanbul ignore if: no need to test this */
		if (!idpGuideData) {
			// wait for idpGuideData to be populated
			return;
		}

		if (
			routeGuide === GuideType.SSO &&
			!idpGuideData.guides?.[routeGuide]?.[routeMethod as Method]
		) {
			setDone(true);
			nav(ROUTES.guideNotFound);
			return;
		}

		// wait for method guide data to be populated
		if (routeGuide === GuideType.SSO && !methodGuideData) return;
		if (routeGuide === GuideType.SCIM && !methodScimGuideData) return;

		const stepGuideNum = Number(routeStepNum);
		if (
			stepGuideNum < 1 ||
			(routeGuide === GuideType.SSO &&
				methodGuideData!.guide.length < stepGuideNum) ||
			(routeGuide === GuideType.SCIM &&
				methodScimGuideData!.guide.length < stepGuideNum)
		) {
			setDone(true);
			nav(ROUTES.guideNotFound);
			return;
		}

		setDone(true);
		setGuideType(routeGuide as GuideType);

		if (routeGuide === GuideType.SSO) {
			setIdPID(routeIdpID);
			setMethod(routeMethod as Method);
			setGuideStepNum(Number(stepGuideNum));
		}
		if (routeGuide === GuideType.SCIM) {
			setScimIdPID(routeIdpID);
			setScimMethod(routeMethod as SCIMMethod);
			setScimGuideStepNum(Number(stepGuideNum));
		}
	}, [
		appConfigLoaded,
		done,
		featureFlagsReady,
		idpGuideData,
		idpsData,
		isJitRoute,
		isSSORoute,
		isSelectIdPRoute,
		methodGuideData,
		methodScimGuideData,
		nav,
		routeGuide,
		routeIdpID,
		routeMethod,
		routeStepNum,
		setGuideStepNum,
		setGuideType,
		setIdPID,
		setMethod,
		setScimGuideStepNum,
		setScimIdPID,
		setScimMethod,
		ssoDisabled,
		isXaaRoute,
		xaaEnabled,
		xaaGuideData,
		xaaIdps,
		setXaaIdPID,
		setXaaMethod,
		setXaaGuideStepNum
	]);

	return useMemo(() => ({ done }), [done]);
};
